import React, { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, Save, X, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', price: '', image: '', category: '', in_stock: true });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => { fetchProducts(); fetchCategories(); }, []);

  const fetchProducts = async () => {
    try { const r = await axios.get(`${API}/products`); setProducts(r.data); } catch (e) { console.error(e); }
  };

  const fetchCategories = async () => {
    try { const r = await axios.get(`${API}/admin/categories`); setCategories(r.data.categories || []); } catch (e) {}
  };

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/upload/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, image: r.data.url }));
      toast.success('תמונה הועלתה');
    } catch (e) { toast.error('שגיאה בהעלאת תמונה'); }
    setUploading(false);
  };

  const saveProduct = async () => {
    try {
      if (editing) {
        await axios.patch(`${API}/admin/products/${editing}`, { ...form, price: parseFloat(form.price) });
        toast.success('מוצר עודכן');
      } else {
        await axios.post(`${API}/products`, { ...form, price: parseFloat(form.price) });
        toast.success('מוצר נוסף');
      }
      setEditing(null); setAdding(false);
      setForm({ name: '', description: '', price: '', image: '', category: '', in_stock: true });
      fetchProducts(); fetchCategories();
    } catch (e) { toast.error('שגיאה בשמירה'); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('למחוק מוצר?')) return;
    try { await axios.delete(`${API}/admin/products/${id}`); toast.success('מוצר נמחק'); fetchProducts(); } catch (e) { toast.error('שגיאה במחיקה'); }
  };

  const startEdit = (p) => {
    setEditing(p.id); setAdding(true);
    setForm({ name: p.name, description: p.description || '', price: p.price, image: p.image || '', category: p.category || '', in_stock: p.in_stock !== false });
  };

  const imgSrc = form.image ? (form.image.startsWith('/') ? process.env.REACT_APP_BACKEND_URL + form.image : form.image) : null;

  return (
    <div dir="rtl" data-testid="admin-products-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-black text-[#2C3E2D]" style={{ fontFamily: 'Heebo, sans-serif' }}>ניהול מוצרים</h2>
        <button onClick={() => { setAdding(true); setEditing(null); setForm({ name: '', description: '', price: '', image: '', category: '', in_stock: true }); }} className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-xl px-4 py-2 text-sm font-bold flex items-center gap-2" data-testid="add-product-btn">
          <Plus className="w-4 h-4" /> הוסף מוצר
        </button>
      </div>

      {adding && (
        <div className="bg-white rounded-xl p-4 mb-4 border border-stone-200 shadow-sm" data-testid="product-form">
          <h3 className="font-bold mb-3">{editing ? 'עריכת מוצר' : 'מוצר חדש'}</h3>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="שם מוצר" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="border rounded-lg px-3 py-2 text-sm" />
            <input placeholder="מחיר" type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})} className="border rounded-lg px-3 py-2 text-sm" />

            {/* Category dropdown */}
            <div>
              <select
                value={form.category}
                onChange={e => setForm({...form, category: e.target.value})}
                className="border rounded-lg px-3 py-2 text-sm w-full"
                data-testid="product-category-select"
              >
                <option value="">בחר קטגוריה</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                <option value="__new__">+ קטגוריה חדשה</option>
              </select>
              {form.category === '__new__' && (
                <input
                  placeholder="שם קטגוריה חדשה"
                  className="border rounded-lg px-3 py-2 text-sm w-full mt-1"
                  onChange={e => setForm({...form, category: e.target.value})}
                  autoFocus
                />
              )}
            </div>

            {/* Image upload */}
            <div>
              <input type="file" ref={fileRef} className="hidden" accept="image/*" onChange={e => { if (e.target.files[0]) uploadImage(e.target.files[0]); }} />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="bg-blue-500 hover:bg-blue-600 text-white rounded-lg px-3 py-2 text-sm font-bold flex items-center gap-1 flex-shrink-0"
                  disabled={uploading}
                  data-testid="upload-image-btn"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? 'מעלה...' : 'העלה תמונה'}
                </button>
                {imgSrc && <img src={imgSrc} alt="" className="w-10 h-10 object-contain rounded border" />}
                {form.image && !form.image.startsWith('/api') && (
                  <input placeholder="או קישור לתמונה" value={form.image} onChange={e => setForm({...form, image: e.target.value})} className="border rounded-lg px-2 py-1.5 text-xs flex-1" dir="ltr" />
                )}
              </div>
            </div>

            <textarea placeholder="תיאור" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="border rounded-lg px-3 py-2 text-sm col-span-2" rows={2} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.in_stock} onChange={e => setForm({...form, in_stock: e.target.checked})} />
              במלאי
            </label>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={saveProduct} className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1"><Save className="w-4 h-4" /> שמור</button>
            <button onClick={() => { setAdding(false); setEditing(null); }} className="bg-stone-200 hover:bg-stone-300 rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1"><X className="w-4 h-4" /> ביטול</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {products.map(p => (
          <div key={p.id} className="bg-white rounded-xl p-3 border border-stone-100 flex items-center gap-3" data-testid={`product-row-${p.id}`}>
            {p.image && <img src={p.image.startsWith('/') ? process.env.REACT_APP_BACKEND_URL + p.image : p.image} alt="" className="w-14 h-14 object-contain rounded-lg bg-stone-50" />}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm text-stone-800">{p.name}</p>
              <p className="text-xs text-stone-400">{p.category}</p>
            </div>
            <span className="text-[#D97706] font-black text-lg">₪{p.price}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${p.in_stock !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {p.in_stock !== false ? 'במלאי' : 'אזל'}
            </span>
            <button onClick={() => startEdit(p)} className="p-2 hover:bg-stone-100 rounded-lg"><Pencil className="w-4 h-4 text-stone-400" /></button>
            <button onClick={() => deleteProduct(p.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminProducts;
